/**
 * Pull image files out of a paste so the composer can attach them.
 *
 * A screenshot arrives as a File whose name is generic or empty, while the
 * attachment validator keys off the file extension. Each image is therefore
 * renamed from its MIME type before it reaches the composer; a real image file
 * copied from the desktop already has a usable name and keeps it.
 */

import { attachmentDisplayClassifier } from "./AttachmentDisplayClassifier.js";

/** MIME types the attachment rules already allow as images. */
const IMAGE_MIME_EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};

const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);

/**
 * @param {string | null | undefined} mimeType
 * @returns {string} allowed extension, or "" when the type is not a usable image
 */
export function extensionForImageMime(mimeType) {
  const mime = String(mimeType || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
  return IMAGE_MIME_EXTENSIONS[mime] ?? "";
}

/**
 * @param {File} file
 * @param {string} extension
 * @param {{ nowMs?: number, index?: number }} [opts]
 * @returns {string}
 */
export function pastedImageFileName(file, extension, { nowMs = Date.now(), index = 1 } = {}) {
  const existing = String(file?.name || "").trim();
  if (existing && IMAGE_EXTENSIONS.has(attachmentDisplayClassifier.extensionFromName(existing))) {
    return existing;
  }
  const suffix = index > 1 ? `-${index}` : "";
  return `pasted-image-${nowMs}${suffix}.${extension}`;
}

/**
 * @param {DataTransfer | null | undefined} clipboardData
 * @returns {File[]} clipboard files, from `files` when present and `items` otherwise
 */
function clipboardFiles(clipboardData) {
  if (!clipboardData) return [];
  if (clipboardData.files?.length) return Array.from(clipboardData.files);
  const items = clipboardData.items ? Array.from(clipboardData.items) : [];
  return items
    .filter((item) => item?.kind === "file")
    .map((item) => item.getAsFile?.())
    .filter(Boolean);
}

/**
 * Image files from a paste, renamed so the attachment validator accepts them.
 * Returns an empty array for text-only pastes, which must keep pasting as text.
 *
 * @param {DataTransfer | null | undefined} clipboardData
 * @param {{ nowMs?: number }} [opts]
 * @returns {File[]}
 */
export function clipboardImageFiles(clipboardData, { nowMs = Date.now() } = {}) {
  const out = [];
  for (const file of clipboardFiles(clipboardData)) {
    const extension = extensionForImageMime(file.type);
    if (!extension) continue;
    const name = pastedImageFileName(file, extension, { nowMs, index: out.length + 1 });
    out.push(name === file.name ? file : new File([file], name, { type: file.type }));
  }
  return out;
}
